<template>
  <div
    v-if="position !== 'hidden'"
    class="scene-list-wrapper"
    :class="[
      `position-${position}`,
      `style-${listStyle}`,
      `size-${thumbnailSize}`,
      { collapsed, collapsible }
    ]"
  >
    <!-- Collapse toggle -->
    <button
      v-if="collapsible"
      class="collapse-toggle"
      :class="`toggle-${position}`"
      @click="collapsed = !collapsed"
      :title="collapsed ? 'Show scenes' : 'Hide scenes'"
    >
      <svg v-if="!collapsed" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path v-if="position === 'bottom'" d="M6 9l6 6 6-6"/>
        <path v-else-if="position === 'right'" d="M9 18l6-6-6-6"/>
        <path v-else-if="position === 'left'" d="M15 18l-6-6 6-6"/>
        <path v-else d="M6 9l6 6 6-6"/>
      </svg>
      <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path v-if="position === 'bottom'" d="M18 15l-6-6-6 6"/>
        <path v-else-if="position === 'right'" d="M15 6l-6 6 6 6"/>
        <path v-else-if="position === 'left'" d="M9 6l6 6-6 6"/>
        <path v-else d="M18 15l-6-6-6 6"/>
      </svg>
      <span class="toggle-label" v-if="collapsed">
        {{ scenes.length }} {{ scenes.length === 1 ? 'Scene' : 'Scenes' }}
      </span>
    </button>

    <!-- Scene list content -->
    <div v-show="!collapsed" class="scene-list-content" ref="scrollContainer">
      <div
        v-for="(scene, index) in scenes"
        :key="scene.id"
        class="scene-item"
        :class="{ active: String(scene.id) === String(activeSceneId) }"
        @click="$emit('select', scene.id)"
      >
        <div class="scene-thumb">
          <img
            v-if="scene.thumbnail || scene.image"
            :src="scene.thumbnail || scene.image"
            :alt="scene.title"
            loading="lazy"
          />
          <div v-else class="scene-thumb-placeholder">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="12" cy="12" r="10"/>
              <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
          </div>
          <div v-if="showCounter" class="scene-number">{{ index + 1 }}</div>
        </div>
        <span class="scene-title" v-if="listStyle === 'list'">{{ scene.title }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { Scene } from '../types'

const props = withDefaults(defineProps<{
  scenes: Scene[]
  activeSceneId: string | null
  position: 'bottom' | 'right' | 'left' | 'hidden'
  listStyle: 'carousel' | 'grid' | 'list' | 'thumbnails'
  thumbnailSize: 'small' | 'medium' | 'large'
  collapsible: boolean
  defaultCollapsed: boolean
  showCounter: boolean
}>(), {
  position: 'bottom',
  listStyle: 'carousel',
  thumbnailSize: 'medium',
  collapsible: true,
  defaultCollapsed: false,
  showCounter: false,
})

defineEmits<{
  select: [sceneId: string]
}>()

const collapsed = ref(props.defaultCollapsed)
const scrollContainer = ref<HTMLElement>()
</script>

<style scoped>
.scene-list-wrapper {
  position: absolute;
  z-index: 12;
  transition: all 0.3s ease;
}

/* Position variants */
.position-bottom {
  bottom: 0;
  left: 0;
  right: 0;
}

.position-right {
  top: 0;
  right: 0;
  bottom: 0;
  width: 200px;
}

.position-left {
  top: 0;
  left: 0;
  bottom: 0;
  width: 200px;
}

.position-bottom .scene-list-content {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  overflow-x: auto;
  overflow-y: hidden;
  background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 80%, transparent 100%);
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.3) transparent;
}

.position-right .scene-list-content,
.position-left .scene-list-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background: rgba(0,0,0,0.75);
  backdrop-filter: blur(8px);
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.3) transparent;
}

/* Collapsed state */
.collapsed .scene-list-content {
  display: none;
}

.collapsed.position-bottom {
  bottom: 0;
}

.collapsed.position-right {
  width: auto;
}

.collapsed.position-left {
  width: auto;
}

/* Collapse toggle */
.collapse-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(8px);
  color: rgba(255,255,255,0.8);
  border: none;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  white-space: nowrap;
}

.collapse-toggle:hover {
  background: rgba(0,0,0,0.85);
  color: white;
}

.toggle-bottom {
  position: absolute;
  top: -32px;
  right: 12px;
  border-radius: 8px 8px 0 0;
}

.toggle-right {
  position: absolute;
  left: -32px;
  top: 12px;
  border-radius: 8px 0 0 8px;
  writing-mode: vertical-lr;
  padding: 12px 6px;
}

.toggle-left {
  position: absolute;
  right: -32px;
  top: 12px;
  border-radius: 0 8px 8px 0;
  writing-mode: vertical-rl;
  padding: 12px 6px;
}

.toggle-label {
  font-weight: 500;
}

/* Scene items */
.scene-item {
  flex-shrink: 0;
  cursor: pointer;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: all 0.2s;
  position: relative;
}

.scene-item:hover {
  border-color: rgba(255,255,255,0.4);
  transform: scale(1.03);
}

.scene-item.active {
  border-color: var(--brand-primary, #3b82f6);
  box-shadow: 0 0 12px rgba(59,130,246,0.4);
}

/* Thumbnail sizes */
.size-small .scene-thumb {
  width: 80px;
  height: 50px;
}

.size-medium .scene-thumb {
  width: 120px;
  height: 70px;
}

.size-large .scene-thumb {
  width: 160px;
  height: 95px;
}

/* Sidebar sizes */
.position-right .scene-thumb,
.position-left .scene-thumb {
  width: 100%;
  height: auto;
  aspect-ratio: 16/9;
}

.scene-thumb {
  position: relative;
  overflow: hidden;
  background: rgba(255,255,255,0.05);
}

.scene-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.scene-thumb-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.3);
}

.scene-number {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0,0,0,0.7);
  color: white;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

/* List style */
.style-list .scene-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 8px;
}

.style-list .scene-title {
  font-size: 12px;
  color: rgba(255,255,255,0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.style-list .scene-item.active .scene-title {
  color: white;
  font-weight: 600;
}

/* Carousel style */
.style-carousel .scene-item {
  display: flex;
  flex-direction: column;
}

/* Grid style */
.style-grid .scene-list-content {
  flex-wrap: wrap;
}

/* Scrollbar styling */
.scene-list-content::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.scene-list-content::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.3);
  border-radius: 2px;
}

.scene-list-content::-webkit-scrollbar-track {
  background: transparent;
}

/* Light mode */
.light .collapse-toggle {
  background: rgba(255,255,255,0.9);
  color: #1e293b;
}

.light .position-bottom .scene-list-content {
  background: linear-gradient(to top, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.6) 80%, transparent 100%);
}

.light .position-right .scene-list-content,
.light .position-left .scene-list-content {
  background: rgba(255,255,255,0.9);
}

.light .scene-thumb-placeholder {
  color: rgba(0,0,0,0.3);
}

.light .style-list .scene-title {
  color: #1e293b;
}

/* Mobile responsive */
@media (max-width: 768px) {
  /* Force bottom layout on mobile for all positions */
  .position-right,
  .position-left {
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: auto;
  }

  .position-right .scene-list-content,
  .position-left .scene-list-content {
    flex-direction: row;
    height: auto;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 8px;
  }

  .position-bottom .scene-list-content {
    padding: 8px 10px;
    gap: 6px;
  }

  /* Collapsed by default on mobile to save space */
  .scene-list-wrapper {
    max-height: 100px;
  }

  .collapsed {
    /* Don't hide wrapper — keep toggle button visible */
    max-height: 40px;
    overflow: visible;
  }

  .collapse-toggle {
    display: flex !important;
  }

  .size-large .scene-thumb,
  .size-medium .scene-thumb {
    width: 80px;
    height: 50px;
    min-width: 80px;
    border-radius: 6px;
  }

  .size-small .scene-thumb {
    width: 60px;
    height: 40px;
    min-width: 60px;
  }
}

@media (max-width: 480px) {
  .size-large .scene-thumb,
  .size-medium .scene-thumb {
    width: 64px;
    height: 42px;
    min-width: 64px;
  }

  .position-bottom .scene-list-content {
    padding: 6px 8px;
    gap: 4px;
  }
}
</style>
